/**
 * useMutationWithToast — v.1-D §7.15, v.3 wiring T304.
 *
 * Wraps a Convex mutation in:
 *   - a fresh correlation ID per invocation (consistent across the
 *     `${logLabel}_started` and `${logLabel}_succeeded` / `_failed` lines),
 *   - structured info / error logs via `lib/logger`,
 *   - success and error sonner toasts (the success toast fires only when
 *     `successMessage` is provided),
 *   - a `Retry` action on the error toast — exposed ONLY when
 *     `options.mutationName` is on the `lib/mutation-policy` whitelist.
 *     Conservative default: when `mutationName` is omitted, `isRetrySafe` is
 *     NOT consulted and Retry is NOT shown.
 *
 * The hook intentionally does NOT pass `correlationId` as a mutation arg
 * (F-2 from §0.4) and does NOT reference an `E_DUPLICATE_CORRELATION` error
 * code (F-3 from §0.4) — both were fabricated invariants in v.1-C.
 *
 * --------------------------------------------------------------------------
 * v.3 PORT-SITE FIX #1 — T106 regression hazard, applied by T304.
 *
 * The frontend-v2 reference at `src/hooks/useMutationWithToast.ts:60` logs
 * the FULL `args` object inside the `_started` payload. That is a P0 FR-003 /
 * FR-023 violation: argument VALUES (titles, secrets, recipient lists) can
 * leak into the log sink. The v.3 contract is "argument keys only, never
 * values" — so this port emits `argKeys: Object.keys(args ?? {})` instead.
 *
 * The negative shape matters too: the `_started` payload MUST NOT contain
 * a property called `args` (the T303 test's load-bearing assertion). The
 * `_failed` payload is held to the same negative invariant — `args` is
 * never logged on either side of the success/failure branch. The `?? {}`
 * guard means a `null` / `undefined` args call surfaces as `argKeys: []`
 * rather than throwing — defensive behaviour pinned by T303.
 * --------------------------------------------------------------------------
 *
 * Ported by T304 from `frontend-v2/src/hooks/useMutationWithToast.ts` with
 * the single line-60 edit above and no other behavioural change. The
 * `frontend-v2` original is left as-is for v.1-D continuity per
 * CLAUDE.md §13.
 */
import { useState } from 'react';
import { useMutation } from 'convex/react';
import type { FunctionReference } from 'convex/server';
import { toast } from 'sonner';
import { newCorrelationId } from '../lib/correlation';
import { logger } from '../lib/logger';
import { isRetrySafe } from '../lib/mutation-policy';

export interface UseMutationWithToastOptions {
  /** Required: stable name used in `${logLabel}_started|succeeded|failed` log records. */
  logLabel: string;
  /** If set, fires `toast.success(successMessage)` on resolve. */
  successMessage?: string;
  /** If set, prepended to the server error string in the failure toast. */
  errorMessagePrefix?: string;
  /**
   * Fully qualified mutation name (e.g. `channels.updateChannel`). Required
   * to surface the Retry button on the error toast — omitting it means
   * Retry is never shown (conservative default).
   */
  mutationName?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyMutation = FunctionReference<'mutation', 'public', any, any>;

export interface UseMutationWithToastResult<M extends AnyMutation> {
  run: (args: M['_args']) => Promise<M['_returnType']>;
  loading: boolean;
}

export function useMutationWithToast<M extends AnyMutation>(
  mutation: M,
  options: UseMutationWithToastOptions,
): UseMutationWithToastResult<M> {
  const mutate = useMutation(mutation);
  const [loading, setLoading] = useState(false);

  async function run(args: M['_args']): Promise<M['_returnType']> {
    const correlationId = newCorrelationId();
    setLoading(true);
    // PORT-SITE FIX #1 (T106 hazard): log argument KEYS only, never values.
    // The `?? {}` guard means an undefined / null args call surfaces as
    // `argKeys: []` rather than throwing on `Object.keys(undefined)`.
    const argKeys = Object.keys((args ?? {}) as object);
    logger.info(`${options.logLabel}_started`, { correlationId, argKeys });
    try {
      const result = await mutate(args);
      logger.info(`${options.logLabel}_succeeded`, { correlationId });
      if (options.successMessage) toast.success(options.successMessage);
      return result as M['_returnType'];
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logger.error(`${options.logLabel}_failed`, {
        correlationId,
        error: message,
      });
      const retrySafe = options.mutationName
        ? isRetrySafe(options.mutationName)
        : false;
      const errorMessage = options.errorMessagePrefix
        ? `${options.errorMessagePrefix}: ${message}`
        : message;
      if (retrySafe) {
        toast.error(errorMessage, {
          action: {
            label: 'Retry',
            onClick: () => {
              void run(args);
            },
          },
        });
      } else {
        toast.error(errorMessage);
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }

  return { run, loading };
}
